import { FFTheme } from "../../../../assets/theme";
import { sidebar } from '../../../../assets/theme/tokens';

interface OwnerState {
  active: boolean;
  transparentSidenav: boolean;
  whiteSidenav: boolean;
  darkMode: boolean;
  miniSidenav?: boolean;
  open?: boolean;
  noCollapse?: boolean;
}

function collapseItem(theme: FFTheme, ownerState: OwnerState) {
  const { palette, transitions, breakpoints, boxShadows, borders, functions } = theme;
  const { active, transparentSidenav, whiteSidenav, darkMode } = ownerState;

  const { white, transparent, dark, grey } = palette as any;
  const { md } = boxShadows;
  const { borderRadius } = borders;
  const { pxToRem, rgba } = functions;

  return {
    background: () => {
      let backgroundValue;

      if (transparentSidenav && darkMode) {
        backgroundValue = active ? rgba(white.main, 0.2) : transparent.main;
      } else if (transparentSidenav && !darkMode) {
        backgroundValue = active ? grey[100] : transparent.main;
      } else if (whiteSidenav) {
        // Steerage sidebar: a light indigo tint behind the active item.
        backgroundValue = active ? sidebar.linkActiveBg : transparent.main;
      } else {
        backgroundValue = active ? rgba(white.main, 0.2) : transparent.main;
      }

      return backgroundValue;
    },
    color: whiteSidenav
      ? active ? sidebar.linkActiveColor : sidebar.linkColor
      : transparentSidenav && !darkMode ? dark.main : white.main,
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: `${pxToRem(8)} ${pxToRem(12)}`,
    margin: `${pxToRem(1.5)} ${pxToRem(12)}`,
    borderRadius: borderRadius.md,
    cursor: "pointer",
    userSelect: "none",
    whiteSpace: "nowrap",
    boxShadow: active && !whiteSidenav && !darkMode && !transparentSidenav ? md : "none",
    [breakpoints.up("xl")]: {
      transition: transitions.create(["box-shadow", "background-color"], {
        easing: transitions.easing.easeInOut,
        duration: transitions.duration.shorter,
      }),
    },
    "&:hover, &:focus": {
      backgroundColor: whiteSidenav
        ? sidebar.linkHoverBg
        : transparentSidenav && !darkMode ? grey[300] : rgba(white.main, 0.2),
    },
  };
}

function collapseIconBox(theme: FFTheme, ownerState: OwnerState) {
  const { palette, transitions, borders, functions } = theme;
  const { transparentSidenav, whiteSidenav, darkMode, active } = ownerState;

  const { white, dark, grey } = palette as any;
  const { borderRadius } = borders;
  const { pxToRem } = functions;

  return {
    minWidth: pxToRem(18),
    minHeight: pxToRem(18),
    color: whiteSidenav ? sidebar.iconColor : transparentSidenav && !darkMode ? grey[500] : white.main,
    borderRadius: borderRadius.md,
    display: "grid",
    placeItems: "center",
    transition: transitions.create("margin", {
      easing: transitions.easing.easeInOut,
      duration: transitions.duration.standard,
    }),
    "& svg, svg g": {
      color: whiteSidenav ? sidebar.iconColor : white.main,
    },
  };
}

const collapseIcon = ({ palette: { white, gradients } }: any, { active, whiteSidenav }: OwnerState) => ({
  // Steerage draws every sidebar icon in the indigo accent.
  color: whiteSidenav ? sidebar.iconColor : active ? white.main : gradients.dark.state,
});

function collapseText(theme: FFTheme, ownerState: OwnerState) {
  const { typography, transitions, breakpoints, functions } = theme;
  const { miniSidenav, transparentSidenav, whiteSidenav, active } = ownerState;

  const { size, fontWeightRegular, fontWeightLight } = typography as any;
  const { pxToRem } = functions;

  return {
    marginLeft: pxToRem(10),
    [breakpoints.up("xl")]: {
      opacity: miniSidenav || (miniSidenav && transparentSidenav) ? 0 : 1,
      maxWidth: miniSidenav || (miniSidenav && transparentSidenav) ? 0 : "100%",
      marginLeft: miniSidenav || (miniSidenav && transparentSidenav) ? 0 : pxToRem(10),
      transition: transitions.create(["opacity", "margin"], {
        easing: transitions.easing.easeInOut,
        duration: transitions.duration.standard,
      }),
    },
    "& span": {
      fontWeight: active ? fontWeightRegular : fontWeightLight,
      fontSize: whiteSidenav ? sidebar.fontSize : size.xs,
      // The template's lineHeight 0 relied on its 12px text; with the larger
      // label it let rows overlap.
      lineHeight: whiteSidenav ? 1.4 : 0,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  };
}

function collapseArrow(theme: FFTheme, ownerState: OwnerState) {
  const { palette, typography, transitions, breakpoints, functions } = theme;
  const { noCollapse, transparentSidenav, whiteSidenav, miniSidenav, open, active, darkMode } = ownerState;

  const { white, dark, grey } = palette as any;
  const { size } = typography as any;
  const { pxToRem, rgba } = functions;

  return {
    fontSize: `${size.lg} !important`,
    fontWeight: 700,
    marginBottom: pxToRem(-1),
    transform: open ? "rotate(0)" : "rotate(-180deg)",
    color: () => {
      let colorValue;

      if (transparentSidenav && darkMode) {
        colorValue = open || active ? white.main : rgba(white.main, 0.25);
      } else if (transparentSidenav || whiteSidenav) {
        colorValue = open || active ? grey[600] : rgba(dark.main, 0.25);
      } else {
        colorValue = open || active ? white.main : rgba(white.main, 0.5);
      }

      return colorValue;
    },
    transition: transitions.create(["color", "transform", "opacity"], {
      easing: transitions.easing.easeInOut,
      duration: transitions.duration.shorter,
    }),
    [breakpoints.up("xl")]: {
      display: noCollapse || (transparentSidenav && miniSidenav) || miniSidenav ? "none !important" : "block !important",
    },
  };
}

export {
  collapseItem,
  collapseIconBox,
  collapseIcon,
  collapseText,
  collapseArrow,
};
